import { useCallback, useMemo } from "react"

import { Form, Formik } from "formik"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { SearchIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { ScrollableLayout, useIsMobileDevice, useMediaQuery, useTheme } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"

import * as Styled from "./CatalogItemsFiltersForm.styled"

const defaultFormValues = {
    tags: [],
}

export function CatalogItemsFiltersForm(props) {

    const {
        activeFilters,
        className,
        close,
        onSubmit,
        project,
        resetFilters,
    } = props

    const { t } = useTranslation([I18N_NAMESPACE])
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const isMobile = useIsMobileDevice()
    const initialFilters = defaultFormValues

    const displayedTags = useMemo(() => {
        if (project?.tags) {
            return project?.tags.sort()
        }
    }, [project])

    const initialValues = useMemo(() => {
        return ({
            tags: activeFilters.tags || [],
            antiTags: activeFilters.antiTags || [],
        })

    }, [activeFilters])

    const getFormFields = useCallback((setFieldValue, values) => {
        return <Styled.InputsWrapper>
            {displayedTags && <>
                <Styled.TagFilter
                    id="tags-filters-select"
                    disableClearable
                    filterSelectedOptions
                    label={t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.inputs.tags.label")}
                    name="tags"
                    options={displayedTags.filter(tag => !values.antiTags?.includes(tag))}
                    setFieldValue={setFieldValue}
                    defaultValue={initialValues?.tags}
                    popupIcon={<SearchIcon />}
                    noOptionsText={t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.inputs.tags.noOptions")}
                />
                <Styled.TagFilter
                    color="action/darkRed"
                    id="antiTags-filters-select"
                    disableClearable
                    filterSelectedOptions
                    label={t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.inputs.antiTags.label")}
                    name="antiTags"
                    options={displayedTags.filter(tag => !values.tags?.includes(tag))}
                    setFieldValue={setFieldValue}
                    defaultValue={initialValues?.antiTags}
                    popupIcon={<SearchIcon />}
                    noOptionsText={t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.inputs.antiTags.noOptions")}
                />
            </>}
        </Styled.InputsWrapper>
    }, [initialValues, t, displayedTags])

    const getFormButtons = useCallback((resetForm) => {
        return <>
            <Styled.Button
                id="reset-filters-button"
                color="error"
                onClick={() => {
                    resetForm({ values: { ...defaultFormValues, ...initialFilters, tags: [], antiTags: [] } })
                    const urlParams = resetFilters()
                    close(urlParams)
                }}
                variant="text"
            >
                {isSmallScreen ? t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.buttons.delete%small")
                    : t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.buttons.delete")}
            </Styled.Button>
            <Styled.Wrapper>
                <Styled.Button
                    id="cancel-filters-button"
                    color="primary"
                    onClick={() => {
                        resetForm({ values: { ...initialValues, ...initialFilters, tags: [], antiTags: [] } })
                        close()
                    }}
                    variant="tertiary"
                >
                    {t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.buttons.cancel")}
                </Styled.Button>
                <Styled.SubmitButton
                    id="submit-filters-button"
                    type="submit"
                >
                    {t(I18N_NAMESPACE, "forms.catalogItemsFiltersForm.buttons.confirm")}
                </Styled.SubmitButton>
            </Styled.Wrapper>
        </>
    }, [
        close,
        initialFilters,
        initialValues,
        isSmallScreen,
        resetFilters,
        t,
    ])

    const getForm = useCallback((resetForm, setFieldValue, values) => {
        if (isMobile) {
            return <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={getFormFields(setFieldValue, values)}
                    buttons={getFormButtons(resetForm)}
                />
            </Styled.Form>
        } else {
            return <Form>
                <Styled.FormWrapper>
                    {getFormFields(setFieldValue, values)}
                    <Styled.ButtonsWrapper>
                        {getFormButtons(resetForm)}
                    </Styled.ButtonsWrapper>
                </Styled.FormWrapper >
            </Form >
        }
    }, [getFormButtons, getFormFields, className, isMobile])


    return <Formik
        className={className}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
    >
        {({ resetForm, setFieldValue, values }) => getForm(resetForm, setFieldValue, values)}
    </Formik>
}

CatalogItemsFiltersForm.propTypes = {
    filters: PropTypes.shape({
        tags: PropTypes.arrayOf(PropTypes.string),
        antiTags: PropTypes.arrayOf(PropTypes.string),
    }),
    className: PropTypes.string,
    formData: PropTypes.shape({
        tags: PropTypes.arrayOf(PropTypes.string),
        antiTags: PropTypes.arrayOf(PropTypes.string),
    }),
    onSubmit: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired,
}